import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  ShoppingBag, Calendar, Layout as LayoutIcon, Palette, LayoutDashboard,
  PhoneCall, Heart, Mail, Gift, FileCheck, Shield, Users, Truck,
  ArrowRight, Sparkles, Lock, Loader2, CheckCircle2, Wheat, Wrench, Scale, Home, Utensils, Dumbbell, Building2,
} from "lucide-react";
import { toast } from "sonner";

const SECTOR_ICON_MAP = { Wheat, Wrench, Scale, Home, Utensils, Dumbbell };

/**
 * Widget registry — chaque entrée est autonome.
 * Pour ajouter un nouveau widget :
 *  1. Ajouter le champ bool dans Settings (backend)
 *  2. Ajouter une entrée ici avec {key, label, description, icon, category, config_path, is_new?, agency_only?}
 *  3. Consommer le flag côté composant public/admin
 */
const WIDGETS = [
  // ----- Boutique -----
  { key: "shop_enabled", category: "Boutique", label: "Boutique en ligne", description: "Vente de produits physiques avec panier et paiement Stripe.", icon: ShoppingBag, color: "#F97316", config_path: "/admin/products" },
  { key: "shop_click_collect_enabled", category: "Boutique", label: "Click & Collect", description: "Retrait en point relais sans frais, avec créneaux horaires.", icon: Truck, color: "#0EA5E9", config_path: "/admin/pickup", depends_on: "shop_enabled" },
  { key: "wishlist_enabled", category: "Boutique", label: "Favoris client", description: "Les visiteurs peuvent sauvegarder leurs produits préférés et recevoir une offre ciblée après N jours.", icon: Heart, color: "#EF4444", config_path: "/admin/wishlist", is_new: true },
  { key: "abandoned_cart_enabled", category: "Boutique", label: "Relance panier abandonné", description: "Email automatique J+1 aux visiteurs qui n'ont pas finalisé leur commande.", icon: Mail, color: "#8B5CF6", config_path: "/admin/settings", is_new: true, not_implemented: true },
  // ----- Réservation -----
  { key: "booking_enabled", category: "Réservation", label: "Réservation en ligne", description: "Vente de créneaux horaires pour les services (acompte, plein tarif, gratuit).", icon: Calendar, color: "#06B6D4", config_path: "/admin/bookings-config" },
  { key: "rental_enabled", category: "Réservation", label: "Location / Réservation de durée", description: "Location d'objets ou hébergements à l'heure/jour/nuit avec unités nommées et calendrier Airbnb-like.", icon: Calendar, color: "#14B8A6", config_path: "/admin/rentals-dashboard", is_new: true },
  // ----- Marketing & Acquisition -----
  { key: "popup_callback_enabled", category: "Marketing & Acquisition", label: "Popup de rétention", description: "Popup 'être rappelé' avec créneau matin / après-midi / fin de journée.", icon: PhoneCall, color: "#10B981", config_path: "/admin/popup-callback", is_new: true },
  { key: "loyalty_enabled", category: "Marketing & Acquisition", label: "Fidélité & cashback", description: "Points à chaque achat, parrainage, cadeau automatique au panier.", icon: Gift, color: "#F59E0B", config_path: "/admin/settings", is_new: true, not_implemented: true },
  // ----- Compliance -----
  { key: "rgpd_generator_enabled", category: "Conformité", label: "Générateur RGPD", description: "Mentions légales et politique de confidentialité générées automatiquement depuis vos informations.", icon: Shield, color: "#14B8A6", config_path: "/admin/legal", is_new: true },
  // ----- Site / UI -----
  { key: "topbar_enabled", category: "Site & UI", label: "Barre supérieure (topbar)", description: "Bandeau desktop au-dessus du header avec infos rapides (horaires, tel, réseaux).", icon: LayoutIcon, color: "#6366F1", config_path: "/admin/header" },
];

const CATEGORIES = ["Boutique", "Réservation", "Marketing & Acquisition", "Conformité", "Site & UI"];

export default function WidgetsConsoleAdmin() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key, value, meta) => {
    if (meta?.not_implemented && value) {
      toast.info(`"${meta.label}" arrive très bientôt — widget en cours de développement.`);
      return;
    }
    if (meta?.depends_on && value && !settings?.[meta.depends_on]) {
      toast.error(`Activez d'abord "${WIDGETS.find(w => w.key === meta.depends_on)?.label}"`);
      return;
    }
    setSaving(key);
    try {
      await api.put("/settings", { ...settings, [key]: value });
      setSettings(s => ({ ...s, [key]: value }));
      toast.success(`${meta.label} ${value ? "activé" : "désactivé"}`);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(null); }
  };

  if (!settings) {
    return <div className="p-10 text-sm text-[hsl(var(--muted-foreground))]">Chargement…</div>;
  }

  const activeCount = WIDGETS.filter(w => settings[w.key]).length;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="widgets-console-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Modules</div>
        <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Console des widgets</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Activez ou désactivez les fonctionnalités de votre site. <strong className="text-[color:var(--brand-navy)]">{activeCount}</strong> widget{activeCount > 1 ? "s" : ""} actif{activeCount > 1 ? "s" : ""} sur {WIDGETS.length}.
        </p>
      </header>

      <SectorPresetPicker currentSector={settings.sector_preset_id} onApplied={() => window.location.reload()} />

      {CATEGORIES.map(cat => {
        const list = WIDGETS.filter(w => w.category === cat);
        if (list.length === 0) return null;
        return (
          <section key={cat} className="space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" />
              <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-[color:var(--brand-navy)]">{cat}</h2>
              <div className="flex-1 h-px bg-[hsl(var(--border))]" />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {list.map(w => {
                const on = !!settings[w.key];
                const disabledByDep = w.depends_on && !settings[w.depends_on];
                return (
                  <Card key={w.key} className={`p-4 transition-all ${on ? "ring-2 ring-[color:var(--brand-sky)]/30 bg-[color:var(--brand-sky)]/5" : "hover:bg-[hsl(var(--muted))]/30"} ${disabledByDep ? "opacity-60" : ""}`} data-testid={`widget-card-${w.key}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${w.color}18`, color: w.color }}>
                        <w.icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-sm text-[color:var(--brand-navy)]">{w.label}</h3>
                          {w.is_new && <Badge className="text-[9px] uppercase tracking-wider bg-gradient-to-br from-[color:var(--brand-sky)] to-[color:var(--brand-navy)] text-white border-0">Nouveau</Badge>}
                          {w.not_implemented && <Badge variant="outline" className="text-[9px] uppercase tracking-wider border-amber-300 text-amber-700 bg-amber-50">Bientôt</Badge>}
                          {w.agency_only && <Badge variant="outline" className="text-[9px] uppercase tracking-wider border-[color:var(--brand-navy)]/30 text-[color:var(--brand-navy)]"><Lock className="w-2.5 h-2.5 mr-0.5" />Agence</Badge>}
                        </div>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{w.description}</p>
                        {disabledByDep && (
                          <p className="text-[11px] text-amber-600 mt-1">Nécessite « {WIDGETS.find(x => x.key === w.depends_on)?.label} » activé</p>
                        )}
                        {on && w.config_path && !w.not_implemented && (
                          <Link to={w.config_path} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[color:var(--brand-sky)] hover:underline mt-1.5" data-testid={`widget-config-${w.key}`}>
                            Configurer<ArrowRight className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                      <Switch
                        checked={on}
                        onCheckedChange={v => toggle(w.key, v, w)}
                        disabled={saving === w.key || disabledByDep}
                        data-testid={`widget-toggle-${w.key}`}
                      />
                    </div>
                  </Card>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// =========================================================
// Sector preset picker — appliqué en 1 clic depuis la console widgets.
// Pré-remplit les settings, ajoute services/blog/FAQs du métier ciblé.
// Ne supprime rien — complète simplement ce qui existe (idempotent).
// =========================================================
function SectorPresetPicker({ currentSector, onApplied }) {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    api.get("/template/sectors")
      .then(r => setSectors(r.data.sectors || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const applyPreset = async (sectorId) => {
    if (!window.confirm(`Appliquer le preset "${sectors.find(s => s.id === sectorId)?.label}" ?\n\nCela va enrichir vos paramètres (hero, couleurs, modules), services, blog et FAQ recommandés.\n\nAucun contenu existant ne sera supprimé.`)) return;
    setApplying(sectorId);
    try {
      await api.post("/template/apply-sector", {
        sector_id: sectorId,
        reset_first: false,
        apply_theme: true,
        seed_content: true,
      });
      toast.success("Preset appliqué — rechargement");
      setTimeout(() => onApplied && onApplied(), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setApplying(null); }
  };

  if (loading) return null;
  if (sectors.length === 0) return null;

  return (
    <Card className="p-5 bg-gradient-to-br from-[color:var(--brand-sky)]/5 to-transparent border-[color:var(--brand-sky)]/20" data-testid="widgets-sector-picker">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[color:var(--brand-sky)]/10 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-[color:var(--brand-sky)]" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Preset secteur</h2>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
            Démarrage accéléré pour votre métier : active les bons widgets, installe des services modèles, propose un thème adapté. Aucun contenu existant n'est écrasé.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {sectors.map(s => {
          const Icon = SECTOR_ICON_MAP[s.icon] || Building2;
          const isActive = currentSector === s.id;
          const isApplying = applying === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => !isApplying && applyPreset(s.id)}
              disabled={isApplying}
              className={`relative text-left p-3 rounded-lg border-2 transition-all ${isActive ? "border-emerald-400 bg-emerald-50" : "border-[hsl(var(--border))] hover:border-[color:var(--brand-sky)]/60 hover:bg-[color:var(--brand-sky)]/5"} ${isApplying ? "opacity-60" : ""}`}
              data-testid={`widget-preset-${s.id}`}
            >
              {isActive && (
                <CheckCircle2 className="w-4 h-4 absolute top-2 right-2 text-emerald-600" />
              )}
              {isApplying && (
                <Loader2 className="w-4 h-4 absolute top-2 right-2 animate-spin text-[color:var(--brand-sky)]" />
              )}
              <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: `${s.color}22`, color: s.color }}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="font-display font-bold text-sm text-[color:var(--brand-navy)] truncate">{s.label}</div>
              <div className="flex gap-1 mt-1 flex-wrap">
                {s.has_shop && <span className="text-[9px] px-1 py-0.5 rounded bg-orange-100 text-orange-700">Boutique</span>}
                {s.has_booking && <span className="text-[9px] px-1 py-0.5 rounded bg-cyan-100 text-cyan-700">Résa</span>}
                {s.has_rental && <span className="text-[9px] px-1 py-0.5 rounded bg-teal-100 text-teal-700">Location</span>}
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

